import React, { useMemo, useState } from "react";
import logoo from "../assets/logo.svg";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

function Header({ onOpenAuth, onOpenMenu }) {
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const params = new URLSearchParams(search);
  const initial = params.get("q") || "";
  const [value, setValue] = useState(initial);
  const token = useSelector((state) => state.auth?.token);
  const isLoggedIn =
    Boolean(token) && token !== "null" && token !== "undefined";

  const [isDark, setIsDark] = useState(() => {
    try {
      const stored = localStorage.getItem("femtyz_theme");
      if (stored === "dark") return true;
      if (stored === "light") return false;
      return window.matchMedia?.("(prefers-color-scheme: dark)")?.matches ?? false;
    } catch {
      return false;
    }
  });

  const hamburgerIcon = useMemo(
    () => (
      <svg
        viewBox="0 0 24 24"
        className="h-5 w-5 shrink-0"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
      >
        <path d="M4 7h16" />
        <path d="M4 12h16" />
        <path d="M4 17h16" />
      </svg>
    ),
    []
  );

  React.useEffect(() => {
    const root = document.documentElement;
    if (isDark) root.classList.add("dark");
    else root.classList.remove("dark");
    try {
      localStorage.setItem("femtyz_theme", isDark ? "dark" : "light");
    } catch {
      // ignore
    }
  }, [isDark]);

  function submitSearch(e) {
    e?.preventDefault();
    if (!isLoggedIn) {
      onOpenAuth?.("login");
      return;
    }
    const q = value.trim();
    if (!q) {
      return;
    }
    navigate(`/search?q=${encodeURIComponent(q)}`);
  }

  return (
    <header className="sticky top-0 z-50 border-b bg-[#F8FAFC] backdrop-blur dark:border-slate-800 dark:bg-slate-950/75">
      <div className="mx-auto w-full max-w-[90rem] px-4 py-3 md:px-6 bg-[#F8FAFC] border-[#E6EAF0] border-[2px]">
        {/* Mobile header (matches screenshot style) */}
        <div className="flex items-center gap-3 md:hidden">
          <button
            type="button"
            onClick={onOpenMenu}
            aria-label="Open menu"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-[#E6EAF0] text-slate-700 dark:bg-slate-900 dark:ring-slate-800 dark:text-slate-100"
          >
            {hamburgerIcon}
          </button>

          <form onSubmit={submitSearch} className="flex min-w-0 flex-1 items-center">
            <div className="flex w-full min-w-0 items-center gap-2 rounded-full border border-[#E6EAF0] bg-white/70 px-3 py-2 shadow-sm dark:border-slate-800 dark:bg-slate-900/70">
              <span
                className="pointer-events-none flex shrink-0 text-[#6E7B86] dark:text-slate-400"
                aria-hidden="true"
              >
                <svg
                  viewBox="0 0 24 24"
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <path d="M21 21l-4.35-4.35" />
                  <circle cx="11" cy="11" r="7" />
                </svg>
              </span>
              <input
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Search, paste or compare"
                className="min-w-0 w-full bg-transparent font-medium text-[13px] text-[#24303A] placeholder:text-[#6E7B86] focus:outline-none dark:text-slate-100 dark:placeholder:text-slate-400"
              />
            </div>
          </form>

          <button
            type="submit"
            aria-label="Search"
            onClick={submitSearch}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#7352C7] text-white shadow-sm"
          >
            <svg
              viewBox="0 0 24 24"
              className="h-5 w-5 shrink-0 text-white"
              fill="none"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="M21 21l-4.35-4.35" />
              <circle cx="11" cy="11" r="7" />
            </svg>
          </button>
        </div>

        {/* Desktop header (existing) */}
        <div className="hidden md:flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Link to="/" className="flex items-center gap-2">
              <img src={logoo} className="h-10 w-auto max-w-[140px]" alt="logo" />
            </Link>
          </div>

          <form onSubmit={submitSearch} className="flex flex-1 items-center justify-center">
            <div className="flex w-full max-w-[560px] items-center gap-2 rounded-full border border-[#E6EAF0] bg-white/70 px-3 py-2 shadow-sm dark:border-slate-800 dark:bg-slate-900/70 md:px-4">
              <span
                className="pointer-events-none flex shrink-0 text-[#6E7B86] dark:text-slate-400"
                aria-hidden="true"
              >
                <svg
                  viewBox="0 0 24 24"
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <path d="M21 21l-4.35-4.35" />
                  <circle cx="11" cy="11" r="7" />
                </svg>
              </span>
              <input
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Paste a list or search (e.g., Milk, Eggs, Bread)..."
                className="w-full bg-transparent font-medium text-[14px] text-[#24303A] placeholder:text-[14px] placeholder:text-[#6E7B86] focus:outline-none dark:text-slate-100 dark:placeholder:text-slate-400"
              />
              <button
                type="submit"
                className="rounded-full bg-[#7352C7] px-4 py-1.5 text-[16px] font-bold text-white"
              >
                Search
              </button>
            </div>
          </form>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setIsDark((v) => !v)}
              aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-[#E6EAF0] text-slate-700 dark:bg-slate-900 dark:ring-slate-800 dark:text-slate-100"
            >
              {isDark ? (
                <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M12 4V2" />
                  <path d="M12 22v-2" />
                  <path d="M4.22 4.22l1.42 1.42" />
                  <path d="M18.36 18.36l1.42 1.42" />
                  <path d="M2 12h2" />
                  <path d="M20 12h2" />
                  <path d="M4.22 19.78l1.42-1.42" />
                  <path d="M18.36 5.64l1.42-1.42" />
                  <circle cx="12" cy="12" r="4" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
                </svg>
              )}
            </button>

            {isLoggedIn ? (
              <button
                type="button"
                aria-label="Profile"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-[#E6EAF0] text-slate-700 dark:bg-slate-900 dark:ring-slate-800 dark:text-slate-100"
                onClick={() => navigate("/settings")}
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <circle cx="12" cy="7" r="4" />
                  <path d="M20 21a8 8 0 10-16 0" />
                </svg>
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="rounded-full border-2 border-[#E6EAF0] shadow-sm bg-white px-6 py-1 text-[16px] font-bold font-semibold text-[#24303A] hover:border-indigo-500 hover:border-femty-primary dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
                  onClick={() => onOpenAuth?.("login")}
                >
                  Sign in
                </button>
                <button
                  type="button"
                  className="rounded-full bg-[#7352C7] px-6 py-1 text-[16px] font-semibold text-white"
                  onClick={() => onOpenAuth?.("signup")}
                >
                  Sign up
                </button>
              </>
            )}

            <button
              type="button"
              onClick={onOpenMenu}
              aria-label="Open menu"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-[#E6EAF0] text-slate-700 dark:bg-slate-900 dark:ring-slate-800 dark:text-slate-100"
            >
              {hamburgerIcon}
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;